import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  restApi,
  useGetSkipTracePreviewQuery,
  useGetSkipTraceSelectionPreviewQuery,
  useGetSkipTraceJobQuery,
  useSkipTraceContactMutation,
  useSkipTraceContactListMutation,
  useSkipTraceSelectionMutation,
} from "@/store/rest_api";
import type { SkipTraceJob } from "@/model/interface/SkipTrace";
import { SkipTraceScope } from "@/enum/SkipTraceScope";
import { SkipTraceJobStatus } from "@/enum/SkipTraceJobStatus";
import { SkipTraceMode } from "@/enum/SkipTraceMode";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface SkipTraceDialogProps {
  scope: SkipTraceScope;
  targetId?: string;
  contactIds?: string[];
  targetName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SkipTraceDialog: React.FC<SkipTraceDialogProps> = ({
  scope,
  targetId,
  contactIds,
  targetName,
  open,
  onOpenChange,
}) => {
  const dispatch = useDispatch();
  const [job, setJob] = useState<SkipTraceJob | null>(null);
  const [mode, setMode] = useState<SkipTraceMode>(SkipTraceMode.INSTANT);
  const isSelection = scope === SkipTraceScope.SELECTION;
  const targetPreview = useGetSkipTracePreviewQuery(
    { scope, targetId: targetId ?? "" },
    { skip: !open || job !== null || isSelection },
  );
  const selectionPreview = useGetSkipTraceSelectionPreviewQuery(contactIds ?? [], {
    skip: !open || job !== null || !isSelection,
  });
  const preview = isSelection ? selectionPreview.data : targetPreview.data;
  const previewLoading = isSelection ? selectionPreview.isFetching : targetPreview.isFetching;
  const previewError = isSelection ? selectionPreview.error : targetPreview.error;
  const [skipTraceContact, { isLoading: tracingContact }] = useSkipTraceContactMutation();
  const [skipTraceContactList, { isLoading: tracingList }] = useSkipTraceContactListMutation();
  const [skipTraceSelection, { isLoading: tracingSelection }] = useSkipTraceSelectionMutation();
  const jobInProgress =
    job !== null && (job.status === SkipTraceJobStatus.PENDING || job.status === SkipTraceJobStatus.RUNNING);
  const { data: polledJob } = useGetSkipTraceJobQuery(job?.id ?? 0, {
    skip: !jobInProgress,
    pollingInterval: 3000,
  });
  const tracing = tracingContact || tracingList || tracingSelection;
  const instantAvailable = !!preview && !preview.bulk && preview.instantRatePerHit !== null;
  const selectedMode = instantAvailable ? mode : SkipTraceMode.DELAYED;
  const maximumTotal = preview
    ? selectedMode === SkipTraceMode.INSTANT
      ? preview.instantMaximumTotal ?? 0
      : preview.delayedMaximumTotal
    : 0;
  const sufficientBalance = !!preview && preview.balance >= maximumTotal;

  const formatMoney = (value: number) => `$${value.toFixed(2)}`;

  const delayedPriceLabel = () => {
    if (!preview) {
      return "";
    }
    if (preview.unnamedCount === 0) {
      return `${formatMoney(preview.delayedRatePerHit)} per hit`;
    }
    if (preview.namedCount === 0) {
      return `${formatMoney(preview.delayedUnnamedRatePerHit)} per hit`;
    }
    return `${formatMoney(preview.delayedRatePerHit)} per hit, ${formatMoney(preview.delayedUnnamedRatePerHit)} without an owner name`;
  };

  const handleRun = async () => {
    try {
      const created = isSelection
        ? await skipTraceSelection({ contactIds: contactIds ?? [], mode: selectedMode }).unwrap()
        : scope === SkipTraceScope.CONTACT
          ? await skipTraceContact({ contactId: targetId ?? "", mode: selectedMode }).unwrap()
          : await skipTraceContactList({ listId: targetId ?? "", mode: selectedMode }).unwrap();
      setJob(created);
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      toast.error(message || "Skip trace failed");
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setJob(null);
      setMode(SkipTraceMode.INSTANT);
    }
    onOpenChange(nextOpen);
  };

  useEffect(() => {
    if (polledJob && polledJob.id === job?.id) {
      setJob(polledJob);
      if (polledJob.status === SkipTraceJobStatus.COMPLETED || polledJob.status === SkipTraceJobStatus.FAILED) {
        dispatch(restApi.util.invalidateTags(["Home", "Billing", "Contacts"]));
      }
    }
  }, [polledJob, job?.id, dispatch]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Skip Trace{targetName ? `: ${targetName}` : ""}</DialogTitle>
          <DialogDescription>
            Finds owner phone numbers, emails and mailing address for the linked property.
          </DialogDescription>
        </DialogHeader>

        {job === null && (
          <div className="space-y-3 text-sm">
            {previewError ? (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {(previewError as { data?: { message?: string } })?.data?.message ||
                  "Could not load the cost preview. Please try again."}
              </div>
            ) : previewLoading || !preview ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Calculating cost...
              </div>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contacts to trace</span>
                  <span className="font-medium">{preview.eligibleCount}</span>
                </div>
                {preview.ineligibleCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {preview.ineligibleCount} contact{preview.ineligibleCount === 1 ? " has" : "s have"} no
                    linked property with a full address and will be skipped.
                  </p>
                )}
                {instantAvailable ? (
                  <div className="grid gap-2" role="radiogroup" aria-label="When to get results">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selectedMode === SkipTraceMode.INSTANT}
                      onClick={() => setMode(SkipTraceMode.INSTANT)}
                      className={cn(
                        "flex items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors",
                        selectedMode === SkipTraceMode.INSTANT ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
                      )}
                    >
                      <span>
                        <span className="block font-medium">Instant</span>
                        <span className="block text-xs text-muted-foreground">Results right away</span>
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">
                        {formatMoney(preview.instantRatePerHit ?? 0)} per hit
                      </span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selectedMode === SkipTraceMode.DELAYED}
                      onClick={() => setMode(SkipTraceMode.DELAYED)}
                      className={cn(
                        "flex items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors",
                        selectedMode === SkipTraceMode.DELAYED ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50",
                      )}
                    >
                      <span>
                        <span className="block font-medium">In a few minutes</span>
                        <span className="block text-xs text-muted-foreground">We notify you when results are in</span>
                      </span>
                      <span className="shrink-0 text-right font-medium tabular-nums">{delayedPriceLabel()}</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Price per hit</span>
                      <span className="text-right font-medium tabular-nums">{delayedPriceLabel()}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Tracing {preview.bulkMinimum} or more contacts runs at the lower price. Results arrive in a few
                      minutes and we notify you when they are in.
                    </p>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Maximum total</span>
                  <span className="font-medium tabular-nums">{formatMoney(maximumTotal)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  You only pay for hits. Nothing found means no charge.
                </p>
                {!sufficientBalance && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Your balance ({formatMoney(preview.balance)}) does not cover the maximum total. Top up
                    before running this trace.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {job !== null && jobInProgress && (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Tracing {job.processedCount} of {job.totalCount}...
            </div>
            <p className="text-xs text-muted-foreground">
              You can close this window. We notify you when results are in.
            </p>
          </div>
        )}

        {job !== null && job.status === SkipTraceJobStatus.COMPLETED && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Trace complete
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border p-2">
                <p className="text-xs text-muted-foreground">Hits</p>
                <p className="font-medium">{job.hitCount}</p>
              </div>
              <div className="rounded-md border p-2">
                <p className="text-xs text-muted-foreground">No hit</p>
                <p className="font-medium">{job.noHitCount}</p>
              </div>
              <div className="rounded-md border p-2">
                <p className="text-xs text-muted-foreground">Phones added</p>
                <p className="font-medium">{job.phonesAdded}</p>
              </div>
              <div className="rounded-md border p-2">
                <p className="text-xs text-muted-foreground">Emails added</p>
                <p className="font-medium">{job.emailsAdded}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Charged {formatMoney(job.costCents / 100)}. Hits are tagged "Skip Traced".
            </p>
          </div>
        )}

        {job !== null && job.status === SkipTraceJobStatus.FAILED && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {job.errorMessage || "Skip trace failed. Please try again."}
          </div>
        )}

        <DialogFooter>
          {job === null ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleRun}
                disabled={
                  tracing ||
                  previewLoading ||
                  !!previewError ||
                  !preview ||
                  preview.eligibleCount === 0 ||
                  !sufficientBalance
                }
              >
                {tracing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Run Skip Trace
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkipTraceDialog;
